import { useIsAgentSessionRecordingEnabled } from '@/features/flags';
import { getCurrentSessionId } from '@/hooks/currentSessionRoute';
import { agentRecordingSessionIdAtom } from '@heidi/agents/data';
import { registerCurrentIdsAdapter } from '@heidi/runtime/shell';
import { useAtomValue } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { createContext, useContext } from 'react';

/**
 * Override context for `useCurrentSessionId`. When a parent provides a non-null
 * value here, any descendant calling `useCurrentSessionId()` reads from this
 * context instead of inspecting the router pathname.
 *
 * Use case: rendering session-page components (SplitScreen / ScribeTabs /
 * NoteTabPane / DocumentTabPane …) outside of `/scribe/session/{id}` — for
 * example inside the linked-context canvas preview on `/scribe/chat` — without
 * forcing every consumer of `useCurrentSessionId` to accept an id as a prop.
 *
 * `null` means "no override; fall back to the router" (default).
 */
export const SessionIdOverrideContext = createContext<string | null>(null);

export function useCurrentSessionId() {
  const overrideId = useContext(SessionIdOverrideContext);
  const router = useRouter();

  if (overrideId) {
    return overrideId;
  }

  // Haiku complete page embeds the Heidi sessionId in router.query.id (catch-all).
  if (
    !router.pathname?.startsWith('/scribe/session') &&
    !router.pathname?.startsWith('/integration/epic/haiku/complete')
  ) {
    return '';
  }

  const { id: routeId } = router.query;

  if (Array.isArray(routeId)) {
    return routeId.at(0) ?? '';
  }
  if (typeof routeId === 'string') {
    return routeId;
  }
  return '';
}

/**
 * Session id the recording engine should act on: the agent-recording override
 * (set by ``AgentSessionRecordingControl`` when the agent drives recording off
 * the session page) when present, else the normal route/override session.
 *
 * Lets the recorder target a session that is not the current route without
 * threading an id through every recording hook. When the override is unset
 * (the default) this is identical to ``useCurrentSessionId()``.
 */
export function useRecordingSessionId() {
  const isAgentSessionRecordingEnabled = useIsAgentSessionRecordingEnabled();
  const agentSessionId = useAtomValue(agentRecordingSessionIdAtom);
  const routeSessionId = useCurrentSessionId();
  // Full no-op when the feature is off: behave EXACTLY like
  // useCurrentSessionId, never consulting the agent override. Every recording
  // path that calls this (useRecordingV1, chunk upload) is then byte-for-byte
  // unchanged from before the feature existed.
  if (!isAgentSessionRecordingEnabled) {
    return routeSessionId;
  }
  // Route session wins when present (a real /scribe/session page); the agent
  // override only applies off the session page (e.g. the projects / agent
  // chat). The overlay clears the override on a full stop, but route-wins is
  // also the backstop: even a left-over override can never misroute a normal
  // session-page recording.
  return routeSessionId || agentSessionId || '';
}

export function useCurrentOfflineSessionId() {
  const searchParams = useSearchParams();
  const sessionId = searchParams.get('id');

  return sessionId;
}

// Re-exported so the many existing `@/hooks/useCurrentIds` callers keep working.
// New leaf-level callers should import it from `@/hooks/currentSessionRoute`
// directly and avoid this module's flag and atom imports.
export { getCurrentSessionId };

/**
 * Fill the `CurrentIds` port in `@heidi/runtime/shell` so package UI can read
 * route identity without reading the router.
 *
 * Registered at module scope, and with the hooks above rather than a
 * reimplementation of them — including `SessionIdOverrideContext`, which stays
 * app-side because the override is a property of where the app mounts a screen.
 * A package screen reading the port therefore gets exactly what an app
 * component calling `useCurrentSessionId()` gets, override included.
 *
 * Registration happens when this module loads, which `_app.tsx`'s static import
 * graph reaches (measured); the port's documented fallbacks cover any window
 * before that. Nothing here changes app behaviour: no existing call site is
 * repointed, and the port is an additional reader of the same hooks.
 */
registerCurrentIdsAdapter({ useCurrentSessionId, getCurrentSessionId });
